/**
 * REST + WebSocket client for the live conference dashboard.
 *
 * Surface mirrors `backend/src/conferenceLive/dashboardRouter.ts` (built
 * in Phase 11). Defining the contract here first lets the frontend lock
 * down its types ahead of the backend.
 *
 * WebSocket protocol:
 *  - Connect via `openStream(roomId, accessToken)`. The server first emits
 *    a `state_snapshot` message with the full `LiveConferenceState`, then
 *    streams incremental `LiveEvent` patches.
 *  - The portal applies the snapshot wholesale and then folds each event
 *    into the in-memory state via `applyEvent()` (see the dashboard).
 *  - The same access-token JWT used for REST is passed as `?token=` since
 *    the WS handshake can't carry a bearer header from the browser.
 */
import { apiFetch } from './client';

// ── Types ─────────────────────────────────────────────────────────────

export interface LiveParticipant {
  memberId: string;
  channelUuid: string;
  displayName: string | null;
  e164: string | null;
  isModerator: boolean;
  isMuted: boolean;
  isTalking: boolean;
  handRaised: boolean;
  joinedAt: string;
}

export interface QaQueueEntry {
  memberId: string;
  channelUuid: string;
  displayName: string | null;
  e164: string | null;
  raisedAt: string;
}

export interface RecordingStreamState {
  active: boolean;
  path: string | null;
  startedAt: string | null;
}

export interface LiveConferenceState {
  roomId: string;
  sessionId: string;
  startedAt: string;
  locked: boolean;
  qaMode: boolean;
  currentSpeakerMemberId: string | null;
  recordingCustomerFacing: RecordingStreamState;
  recordingBackend: RecordingStreamState;
  participants: LiveParticipant[];
  qaQueue: QaQueueEntry[];
}

/**
 * Incremental events sent over the WS after the snapshot. Each event
 * type carries just the delta — the client folds it into the cached
 * state. Naming mirrors `conference::maintenance` action verbs so the
 * backend's normalised event stream maps 1:1.
 */
export type LiveEvent =
  | { kind: 'state_snapshot'; state: LiveConferenceState }
  | { kind: 'join'; participant: LiveParticipant; at: string }
  | { kind: 'leave'; memberId: string; at: string }
  | { kind: 'mute'; memberId: string; isMuted: boolean; at: string }
  | { kind: 'talking'; memberId: string; isTalking: boolean; at: string }
  | { kind: 'kick'; memberId: string; at: string }
  | { kind: 'lock'; locked: boolean; at: string }
  | { kind: 'qa_mode'; qaMode: boolean; at: string }
  | { kind: 'hand_raised'; entry: QaQueueEntry; at: string }
  | { kind: 'hand_lowered'; memberId: string; at: string }
  | { kind: 'speaker_opened'; memberId: string; at: string }
  | { kind: 'speaker_closed'; memberId: string; at: string }
  | { kind: 'queue_reordered'; ordered: string[]; at: string }
  | {
      kind: 'recording';
      which: 'customer' | 'backend';
      state: RecordingStreamState;
      at: string;
    }
  | { kind: 'dial'; sofiaTarget: string; at: string }
  | { kind: 'play'; path: string; at: string }
  | { kind: 'end'; at: string };

// ── REST verbs ────────────────────────────────────────────────────────

interface MutateOpts {
  roomId: string;
  memberId: string;
}

interface DialOpts {
  roomId: string;
  sofiaTarget: string;
  asModerator?: boolean;
}

interface PlayOpts {
  roomId: string;
  /** Raw FS path (legacy/power-user). Either this or `recordingId` is required. */
  path?: string;
  /**
   * Recording UUID — the backend resolves it through the active
   * storage driver to a local FS path before issuing the FS verb.
   * Preferred over `path` because it survives storage-driver changes.
   */
  recordingId?: string;
}

interface RecordingOpts {
  roomId: string;
  action: 'start' | 'stop';
}

interface CallOnHandOpts {
  roomId: string;
  /** When omitted the head-of-queue is opened. */
  memberId?: string;
}

interface ReorderOpts {
  roomId: string;
  /** Full new order; member ids that don't exist any more are dropped server-side. */
  ordered: string[];
}

export const conferenceLive = {
  getLive: (roomId: string) =>
    apiFetch<LiveConferenceState>(`/api/conferences/${roomId}/live`),

  mute: ({ roomId, memberId }: MutateOpts) =>
    apiFetch<void>(`/api/conferences/${roomId}/mute`, {
      method: 'POST',
      body: JSON.stringify({ member_id: memberId }),
    }),
  unmute: ({ roomId, memberId }: MutateOpts) =>
    apiFetch<void>(`/api/conferences/${roomId}/unmute`, {
      method: 'POST',
      body: JSON.stringify({ member_id: memberId }),
    }),
  kick: ({ roomId, memberId }: MutateOpts) =>
    apiFetch<void>(`/api/conferences/${roomId}/kick`, {
      method: 'POST',
      body: JSON.stringify({ member_id: memberId }),
    }),

  muteAll: (roomId: string) =>
    apiFetch<void>(`/api/conferences/${roomId}/mute-all`, { method: 'POST' }),
  unmuteAll: (roomId: string) =>
    apiFetch<void>(`/api/conferences/${roomId}/unmute-all`, { method: 'POST' }),

  lock: (roomId: string) =>
    apiFetch<void>(`/api/conferences/${roomId}/lock`, { method: 'POST' }),
  unlock: (roomId: string) =>
    apiFetch<void>(`/api/conferences/${roomId}/unlock`, { method: 'POST' }),
  end: (roomId: string) =>
    apiFetch<void>(`/api/conferences/${roomId}/end`, { method: 'POST' }),

  setRecording: ({ roomId, action }: RecordingOpts) =>
    apiFetch<void>(`/api/conferences/${roomId}/recording`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),

  dial: ({ roomId, sofiaTarget, asModerator }: DialOpts) =>
    apiFetch<void>(`/api/conferences/${roomId}/dial`, {
      method: 'POST',
      body: JSON.stringify({
        sofia_target: sofiaTarget,
        as_moderator: !!asModerator,
      }),
    }),
  play: ({ roomId, path, recordingId }: PlayOpts) =>
    apiFetch<void>(`/api/conferences/${roomId}/play`, {
      method: 'POST',
      body: JSON.stringify(
        recordingId ? { recording_id: recordingId } : { path },
      ),
    }),

  // ── Q&A queue ─────────────────────────────────────────────────────
  callOnHand: ({ roomId, memberId }: CallOnHandOpts) =>
    apiFetch<{ called: { memberId: string } | null }>(
      `/api/conferences/${roomId}/queue/call`,
      {
        method: 'POST',
        body: JSON.stringify(memberId ? { member_id: memberId } : {}),
      },
    ),
  reorderQueue: ({ roomId, ordered }: ReorderOpts) =>
    apiFetch<void>(`/api/conferences/${roomId}/queue/reorder`, {
      method: 'POST',
      body: JSON.stringify({ ordered }),
    }),
  lowerHand: ({ roomId, memberId }: { roomId: string; memberId: string }) =>
    apiFetch<void>(
      `/api/conferences/${roomId}/queue/${encodeURIComponent(memberId)}`,
      { method: 'DELETE' },
    ),

  /**
   * Opens a WebSocket to the live event stream for this room. The auth
   * token rides in the query string because browsers can't set request
   * headers on the WS handshake. The caller is responsible for closing
   * the socket on unmount.
   */
  openStream: (roomId: string, accessToken: string | null | undefined): WebSocket => {
    const proto = typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? 'wss:'
      : 'ws:';
    const host = typeof window !== 'undefined' ? window.location.host : '';
    const qs = accessToken ? `?token=${encodeURIComponent(accessToken)}` : '';
    return new WebSocket(`${proto}//${host}/api/conferences/${roomId}/stream${qs}`);
  },
};
